import { TransactionDetails } from '../../../../types/api.types';
import './InstallmentBreakdowns.scss';
import { useState, useEffect } from 'react';
import { CardElement } from '../../CardElement/CardElement';
import { Cybersource } from '../../../Cybersource/Cybersource';
import { useDispatch } from 'react-redux';
import {
  SET_PAYMENT_DETAILS,
  setInstallmentBins,
  setSelectedPlan,
} from '../../../../redux/paymentActions';
import {
  getInstallmentPlansAPI,
  selectInstallmentPlanAPI,
} from '../../../../services/installment.service';
import { Loader } from '../../../../pages/Loader/Loader';

interface Bank {
  bank_id: string;
  name: string;
  display_name: string;
  logo: string;
}

export interface CardOptions {
  details: TransactionDetails;
  response: any;
  bankId: string;
  selectedBank?: Bank;
  selectedPlan: string;
  providerKey: string;
  onPlanSelect: (planId: string, provider: string, period?: number) => void;
  onBankSelect: (bankId: string) => void;
  cardResponse: any;
}

interface InstallmentPlan {
  id: number;
  period: number;
  fee_percentage: string;
  is_customer_pays_fee: boolean;
  monthly_amount: number;
  total_payable_amount: number;
}

export const InstallmentBreakdown = ({
  details,
  response,
  bankId,
  selectedBank,
  selectedPlan,
  providerKey,
  onPlanSelect,
  onBankSelect,
  cardResponse,
}: CardOptions) => {
  const [plans, setPlans] = useState<InstallmentPlan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchPlans = async () => {
      setIsLoading(true);
      try {
        const plansResponse = await getInstallmentPlansAPI(
          details.data.onepay_transaction_id,
          bankId
        );
        if (plansResponse && plansResponse.status === 200) {
          setPlans(plansResponse.data);
        }
      } catch (error) {
        console.error('Error fetching installment plans:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (bankId) {
      fetchPlans();
    }
  }, [details.data.onepay_transaction_id, bankId]);

  const handlePlanSelection = async (plan: InstallmentPlan) => {
    setIsLoading(true);

    try {
      // Call select plan API
      const selectPlanResponse = await selectInstallmentPlanAPI(
        details.data.onepay_transaction_id,
        bankId,
        plan.id
      );

      if (
        selectPlanResponse &&
        selectPlanResponse.status === 200 &&
        selectPlanResponse.data.length > 0
      ) {
        const planData = selectPlanResponse.data[0];

        // Store the BIN data and other installment info
        dispatch(setInstallmentBins(planData));

        // Store the selected plan ID
        dispatch(
          setSelectedPlan({
            selectedPlanId: plan.id.toString(),
            providerKey: planData.provider_key,
            providerID : planData.provider_id
          })
        );

        // Update the payment details with the monthly amount
        dispatch({
          type: SET_PAYMENT_DETAILS,
          payload: {
            data: {
              ...details.data,
              request_amount: plan.monthly_amount.toString(),
            },
          },
        });

        // Notify parent component about plan selection
        onPlanSelect(plan.id.toString(), planData.provider_key, plan.period);
      }
    } catch (error) {
      console.error('Error selecting installment plan:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {!selectedPlan && (
        <>
          <div className="installment-option mb-4">
            Installment plans <span>Credit Card Only</span>
          </div>

          {/* Bank Header */}
          {selectedBank && (
            <div className="mb-3 ms-2 me-2">
              <div className="choose-payment mb-2">
                Choose a Payment Plan* Provided by
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <img
                    src={selectedBank.logo}
                    alt={selectedBank.display_name}
                    style={{
                      height: '27px',
                      maxWidth: '120px',
                      objectFit: 'contain',
                    }}
                  />
                </div>
                <div
                  onClick={() => onBankSelect('')}
                  style={{ cursor: 'pointer' }}
                  className="select-bank-link"
                >
                  Select Bank
                </div>
              </div>
            </div>
          )}

          {isLoading && <Loader />}

          {!isLoading &&
            plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => handlePlanSelection(plan)}
                className="installment-breakdowns d-flex justify-content-around"
              >
                <div className="months">{plan.period}</div>
                <div className="months-txt">Months Plan</div>
                <div className="d-flex flex-column">
                  <div className="breakdown-txt">
                    {plan.period} x Rs. {plan.monthly_amount.toFixed(2)}
                  </div>
                  <div className="interest-txt">
                    <span
                      style={
                        !plan.is_customer_pays_fee
                          ? { textDecoration: 'line-through' }
                          : {}
                      }
                    >
                      @ {plan.fee_percentage}% Interest
                    </span>
                  </div>
                </div>
              </div>
            ))}

          {/* Footnote */}
          {!isLoading && plans.length > 0 && (
            <div className="mt-3 px-3 choose-payment mb-2">
              *Amount charged upfront. Bank will convert to installments. Extra charges may apply.
            </div>
          )}
        </>
      )}

      {selectedPlan && isLoading && <Loader />}
      {selectedPlan && !isLoading && providerKey === 'CYBERSOURCE-INSTALLMENT-LKR' && (
        <Cybersource />
      )}
      {selectedPlan && !isLoading && providerKey !== 'CYBERSOURCE-INSTALLMENT-LKR' && (
        <CardElement response={cardResponse} />
      )}
    </div>
  );
};
